import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function Hero() {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMenuEnter = (menuName: string) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setOpenMenu(menuName);
  };

  const handleMenuLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setOpenMenu(null);
    }, 300);
  };

  const scrollToConsultation = () => {
    const element = document.getElementById('consultation');
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setOpenMenu(null);
    setIsMobileMenuOpen(false);
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay and Parallax Effect */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4A90E2]/20 via-transparent to-[#E8F4F8]/30 animate-pulse-slow"></div>
        <img 
          src="https://public.readdy.ai/ai/img_res/ca648e97f407a5e3f81c3136f5bf0d70.jpg"
          alt="케어인슈 배경"
          className="w-full h-full object-cover object-center transform scale-105 animate-ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/30 to-white/35"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-[#4A90E2]/10 rounded-full animate-float"></div>
        <div className="absolute top-40 right-20 w-32 h-32 bg-[#E8F4F8]/20 rounded-full animate-float-delayed"></div>
        <div className="absolute bottom-40 left-1/4 w-24 h-24 bg-[#4A90E2]/15 rounded-full animate-float-slow"></div>
      </div>
      
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 py-4 md:py-5 transition-all duration-500 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center transform hover:scale-105 transition-transform duration-300">
            <img 
              src="https://static.readdy.ai/image/b2717fa299e25af1bb585f7256441db5/754657532bad912eeb6a0b9462e02f95.png" 
              alt="케어인슈 로고" 
              className={`h-10 md:h-13 transition-all duration-300 ${
                isScrolled ? '' : 'brightness-0'
              }`}
              style={isScrolled ? {} : { filter: 'brightness(0)' }}
            />
          </Link>
          
          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center gap-8">
            {/* 케어인슈 소개 */}
            <div 
              className="relative"
              onMouseEnter={() => handleMenuEnter('intro')}
              onMouseLeave={handleMenuLeave}
            >
              <button className={`${
                isScrolled ? 'text-gray-900 hover:text-[#4A90E2]' : 'text-gray-900 hover:text-[#4A90E2]'
              } transition-all duration-300 text-base font-medium cursor-pointer whitespace-nowrap flex items-center gap-1 hover:scale-105`}>
                케어인슈 소개
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${openMenu === 'intro' ? 'rotate-180' : ''}`}></i>
              </button>
              {openMenu === 'intro' && (
                <div 
                  className="absolute top-full left-0 pt-2 animate-fade-in-down"
                  onMouseEnter={() => handleMenuEnter('intro')}
                  onMouseLeave={handleMenuLeave}
                >
                  <div className="bg-white rounded-2xl shadow-2xl py-3 min-w-[180px] border border-gray-100">
                    <button
                      onClick={() => scrollToSection('philosophy')}
                      className="w-full text-left px-5 py-3 text-base text-gray-700 hover:bg-gray-50 hover:text-[#4A90E2] transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      케어인슈의 철학
                    </button>
                    <button
                      onClick={() => scrollToSection('why-careinsure')}
                      className="w-full text-left px-5 py-3 text-base text-gray-700 hover:bg-gray-50 hover:text-[#4A90E2] transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      왜 케어인슈인가
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 상품 안내 */}
            <div 
              className="relative"
              onMouseEnter={() => handleMenuEnter('insurance')}
              onMouseLeave={handleMenuLeave}
            >
              <button className={`${
                isScrolled ? 'text-gray-900 hover:text-[#4A90E2]' : 'text-gray-900 hover:text-[#4A90E2]'
              } transition-all duration-300 text-base font-medium cursor-pointer whitespace-nowrap flex items-center gap-1 hover:scale-105`}>
                상품 안내
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${openMenu === 'insurance' ? 'rotate-180' : ''}`}></i>
              </button>
              {openMenu === 'insurance' && (
                <div 
                  className="absolute top-full left-0 pt-2 animate-fade-in-down"
                  onMouseEnter={() => handleMenuEnter('insurance')}
                  onMouseLeave={handleMenuLeave}
                >
                  <div className="bg-white rounded-2xl shadow-2xl py-3 min-w-[240px] border border-gray-100">
                    <button
                      onClick={() => scrollToSection('insurance-details')}
                      className="w-full text-left px-5 py-3 text-base text-gray-700 hover:bg-gray-50 hover:text-[#4A90E2] transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      장기요양기관 배상책임보험이란?
                    </button>
                    <button
                      onClick={() => scrollToSection('product-intro')}
                      className="w-full text-left px-5 py-3 text-base text-gray-700 hover:bg-gray-50 hover:text-[#4A90E2] transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      케어인슈 상품 소개
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 사고 기준 & 손해사정 */}
            <div 
              className="relative"
              onMouseEnter={() => handleMenuEnter('system')}
              onMouseLeave={handleMenuLeave}
            >
              <button className={`${
                isScrolled ? 'text-gray-900 hover:text-[#4A90E2]' : 'text-gray-900 hover:text-[#4A90E2]'
              } transition-all duration-300 text-base font-medium cursor-pointer whitespace-nowrap flex items-center gap-1 hover:scale-105`}>
                사고 기준 & 손해사정
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${openMenu === 'system' ? 'rotate-180' : ''}`}></i>
              </button>
              {openMenu === 'system' && (
                <div 
                  className="absolute top-full left-0 pt-2 animate-fade-in-down"
                  onMouseEnter={() => handleMenuEnter('system')}
                  onMouseLeave={handleMenuLeave}
                >
                  <div className="bg-white rounded-2xl shadow-2xl py-3 min-w-[200px] border border-gray-100">
                    <button
                      onClick={() => scrollToSection('accident-criteria')}
                      className="w-full text-left px-5 py-3 text-base text-gray-700 hover:bg-gray-50 hover:text-[#4A90E2] transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      사고의 3가지 구분
                    </button>
                    <button
                      onClick={() => scrollToSection('claims-process')}
                      className="w-full text-left px-5 py-3 text-base text-gray-700 hover:bg-gray-50 hover:text-[#4A90E2] transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      손해사정 프로세스
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 보험료의 진실 */}
            <div 
              className="relative"
              onMouseEnter={() => handleMenuEnter('benefits')}
              onMouseLeave={handleMenuLeave}
            >
              <button className={`${
                isScrolled ? 'text-gray-900 hover:text-[#4A90E2]' : 'text-gray-900 hover:text-[#4A90E2]'
              } transition-all duration-300 text-base font-medium cursor-pointer whitespace-nowrap flex items-center gap-1 hover:scale-105`}>
                보험료의 진실
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${openMenu === 'benefits' ? 'rotate-180' : ''}`}></i>
              </button>
              {openMenu === 'benefits' && (
                <div 
                  className="absolute top-full left-0 pt-2 animate-fade-in-down"
                  onMouseEnter={() => handleMenuEnter('benefits')}
                  onMouseLeave={handleMenuLeave}
                >
                  <div className="bg-white rounded-2xl shadow-2xl py-3 min-w-[200px] border border-gray-100">
                    <button
                      onClick={() => scrollToSection('premium-reasons')}
                      className="w-full text-left px-5 py-3 text-base text-gray-700 hover:bg-gray-50 hover:text-[#4A90E2] transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      보험료가 낮아지는 이유
                    </button>
                    <button
                      onClick={() => scrollToSection('premium-structure')}
                      className="w-full text-left px-5 py-3 text-base text-gray-700 hover:bg-gray-50 hover:text-[#4A90E2] transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      합리적인 보험료 체계
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 평가·운영 지원 */}
            <button 
              onClick={() => scrollToSection('evaluation')}
              className={`${
                isScrolled ? 'text-gray-900 hover:text-[#4A90E2]' : 'text-gray-900 hover:text-[#4A90E2]'
              } transition-all duration-300 text-base font-medium cursor-pointer whitespace-nowrap hover:scale-105`}
            >
              평가·운영 지원
            </button>

            {/* 고객센터 */}
            <div 
              className="relative"
              onMouseEnter={() => handleMenuEnter('customer')}
              onMouseLeave={handleMenuLeave}
            >
              <button className={`${
                isScrolled ? 'text-gray-900 hover:text-[#4A90E2]' : 'text-gray-900 hover:text-[#4A90E2]'
              } transition-all duration-300 text-base font-medium cursor-pointer whitespace-nowrap flex items-center gap-1 hover:scale-105`}>
                고객센터
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${openMenu === 'customer' ? 'rotate-180' : ''}`}></i>
              </button>
              {openMenu === 'customer' && (
                <div 
                  className="absolute top-full left-0 pt-2 animate-fade-in-down"
                  onMouseEnter={() => handleMenuEnter('customer')}
                  onMouseLeave={handleMenuLeave}
                >
                  <div className="bg-white rounded-2xl shadow-2xl py-3 min-w-[140px] border border-gray-100">
                    <Link
                      to="/notices"
                      className="block w-full text-left px-5 py-3 text-base text-gray-700 hover:bg-gray-50 hover:text-[#4A90E2] transition-all duration-300 cursor-pointer whitespace-nowrap"
                      onClick={() => {
                        setOpenMenu(null);
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      공지사항
                    </Link>
                    <Link
                      to="/resources"
                      className="block w-full text-left px-5 py-3 text-base text-gray-700 hover:bg-gray-50 hover:text-[#4A90E2] transition-all duration-300 cursor-pointer whitespace-nowrap"
                      onClick={() => {
                        setOpenMenu(null);
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      자료실
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-900 cursor-pointer hover:scale-110 transition-transform duration-300"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t border-gray-100 animate-fade-in-down">
            <div className="px-4 py-4 space-y-2">
              <div className="border-b border-gray-100 pb-2">
                <button
                  onClick={() => scrollToSection('philosophy')}
                  className="w-full text-left px-4 py-3 text-base text-gray-700 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                >
                  케어인슈의 철학
                </button>
                <button
                  onClick={() => scrollToSection('why-careinsure')}
                  className="w-full text-left px-4 py-3 text-base text-gray-700 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                >
                  왜 케어인슈인가
                </button>
              </div>
              
              <div className="border-b border-gray-100 pb-2">
                <button
                  onClick={() => scrollToSection('insurance-details')}
                  className="w-full text-left px-4 py-3 text-base text-gray-700 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                >
                  장기요양기관 배상책임보험이란?
                </button>
                <button
                  onClick={() => scrollToSection('product-intro')}
                  className="w-full text-left px-4 py-3 text-base text-gray-700 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                >
                  케어인슈 상품 소개
                </button>
              </div>
              
              <div className="border-b border-gray-100 pb-2">
                <button
                  onClick={() => scrollToSection('accident-criteria')}
                  className="w-full text-left px-4 py-3 text-base text-gray-700 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                >
                  사고의 3가지 구분
                </button>
                <button
                  onClick={() => scrollToSection('claims-process')}
                  className="w-full text-left px-4 py-3 text-base text-gray-700 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                >
                  손해사정 프로세스
                </button>
              </div>
              
              <div className="border-b border-gray-100 pb-2">
                <button
                  onClick={() => scrollToSection('premium-reasons')}
                  className="w-full text-left px-4 py-3 text-base text-gray-700 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                >
                  보험료가 낮아지는 이유
                </button>
                <button
                  onClick={() => scrollToSection('premium-structure')}
                  className="w-full text-left px-4 py-3 text-base text-gray-700 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                >
                  합리적인 보험료 체계
                </button>
              </div>
              
              <button
                onClick={() => scrollToSection('evaluation')}
                className="w-full text-left px-4 py-3 text-base text-gray-700 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
              >
                평가·운영 지원
              </button>
              
              <div className="border-b border-gray-100 pb-2">
                <Link
                  to="/notices"
                  className="block w-full text-left px-4 py-3 text-base text-gray-700 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  공지사항
                </Link>
                <Link
                  to="/resources"
                  className="block w-full text-left px-4 py-3 text-base text-gray-700 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  자료실
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center w-full pt-20 md:pt-20">
        <div className="mb-6 md:mb-8 animate-fade-in-down">
          <span className="inline-block px-4 md:px-5 py-1.5 md:py-2 bg-[#4A90E2]/10 text-[#2563EB] text-base md:text-2xl font-semibold rounded-full hover:bg-[#4A90E2]/20 transition-all duration-300 hover:scale-105">
            장기요양기관 배상책임보험의 새로운 기준
          </span>
        </div>
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 md:mb-8 animate-title-reveal animate-text-glow" style={{ lineHeight: '1.2', letterSpacing: '-0.02em' }}>
          케어인슈<br />
          장기요양기관 배상책임보험
        </h1>
        <p className="mb-12 md:mb-16 animate-fade-in-up text-base md:text-lg text-gray-700">
          보험료는 낮추면서도 시설과 전문인의 권익을 적극적으로 보호하는<br className="hidden md:block" />
          장기요양기관 배상책임보험의 새로운 패러다임
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 animate-fade-in-up px-4">
          <button 
            onClick={scrollToConsultation}
            className="w-full sm:w-auto px-8 md:px-10 py-4 md:py-5 bg-[#4A90E2] text-white text-sm md:text-base font-semibold rounded-2xl hover:bg-[#357ABD] transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            무료 상담 신청하기
          </button>
          <button 
            onClick={() => scrollToSection('product-intro')}
            className="w-full sm:w-auto px-8 md:px-10 py-4 md:py-5 bg-white text-gray-900 text-sm md:text-base font-semibold rounded-2xl hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 cursor-pointer whitespace-nowrap border border-gray-200"
          >
            자세히 알아보기
          </button>
        </div>

        {/* Key Features */}
        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
          <div className="bg-white p-8 md:p-10 rounded-3xl shadow-sm border border-gray-100 hover:shadow-2xl hover:-translate-y-3 transition-all duration-500 animate-fade-in-up group">
            <div className="w-12 h-12 md:w-14 md:h-14 bg-[#E8F4F8] rounded-2xl flex items-center justify-center mb-4 md:mb-6 mx-auto group-hover:bg-[#4A90E2] transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
              <i className="ri-shield-check-line text-2xl md:text-3xl text-[#4A90E2] group-hover:text-white transition-colors duration-300"></i>
            </div>
            <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-[#4A90E2] transition-colors duration-300">경험 요율 기반 보험료</h3>
            <p className="text-gray-600">
              20년 자사경험요율로 설계된<br />합리적인 보험료 구조
            </p>
          </div>
          <div className="bg-white p-8 md:p-10 rounded-3xl shadow-sm border border-gray-100 hover:shadow-2xl hover:-translate-y-3 transition-all duration-500 animate-fade-in-up group" style={{ animationDelay: '0.1s' }}>
            <div className="w-12 h-12 md:w-14 md:h-14 bg-[#E8F4F8] rounded-2xl flex items-center justify-center mb-4 md:mb-6 mx-auto group-hover:bg-[#4A90E2] transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
              <i className="ri-refresh-line text-2xl md:text-3xl text-[#4A90E2] group-hover:text-white transition-colors duration-300"></i>
            </div>
            <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-[#4A90E2] transition-colors duration-300">재가입 보장</h3>
            <p className="text-gray-600">
              사고 발생 시에도<br />합리적 할증으로 계속 가입 가능
            </p>
          </div>
          <div className="bg-white p-8 md:p-10 rounded-3xl shadow-sm border border-gray-100 hover:shadow-2xl hover:-translate-y-3 transition-all duration-500 animate-fade-in-up group" style={{ animationDelay: '0.2s' }}>
            <div className="w-12 h-12 md:w-14 md:h-14 bg-[#E8F4F8] rounded-2xl flex items-center justify-center mb-4 md:mb-6 mx-auto group-hover:bg-[#4A90E2] transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
              <i className="ri-scales-3-line text-2xl md:text-3xl text-[#4A90E2] group-hover:text-white transition-colors duration-300"></i>
            </div>
            <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-[#4A90E2] transition-colors duration-300">명확한 책임 구분</h3>
            <p className="text-gray-600">
              전담 손해사정사의 정확한<br />사고 분석과 책임 판단
            </p>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 md:bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-2xl md:text-3xl text-gray-400"></i>
      </div>
    </section>
  );
}
